/**
 * Carnaf API client.
 *
 * Wraps fetch with:
 *  - Authorization header from the Zustand auth store
 *  - Refresh-on-401 retry (one attempt; bails to login on failure)
 *  - Canonical { success, data, error, code } envelope unwrap
 *
 * Avoids axios for bundle size — fetch + tiny wrapper is enough.
 */

import type { ObdScanResult } from '@tendedloop/shared';
import { useAuthStore } from '@/stores/auth.store';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function rawFetch<T>(path: string, init: RequestInit = {}, withAuth: boolean = true): Promise<T> {
  const headers = new Headers(init.headers);
  // Only set application/json when there's actually a body to parse. Fastify's
  // content-type parser rejects requests that declare JSON content type with
  // an empty body — which is the case for DELETE / bodyless GET. Setting the
  // header conditionally avoids the 400 "Body cannot be empty" rejection.
  if (init.body != null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (withAuth) {
    const token = useAuthStore.getState().accessToken;
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const body = (await res.json().catch(() => ({}))) as ApiEnvelope<T>;

  if (!res.ok || !body.success) {
    throw new ApiError(body.error ?? `HTTP ${res.status}`, res.status, body.code);
  }
  return body.data as T;
}

/** Public API: request that re-fetches if a 401 indicates a stale access token. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  try {
    return await rawFetch<T>(path, init, true);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      // Try one refresh; if it fails the user lands at /login.
      const refreshed = await tryRefresh();
      if (refreshed) {
        return rawFetch<T>(path, init, true);
      }
      useAuthStore.getState().clear();
    }
    throw err;
  }
}

/** Anonymous request (no Authorization header). */
export async function apiAnon<T>(path: string, init: RequestInit = {}): Promise<T> {
  return rawFetch<T>(path, init, false);
}

async function tryRefresh(): Promise<boolean> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return false;

  try {
    const data = await rawFetch<{
      tokens: { accessToken: string; refreshToken: string };
    }>('/api/auth/carnaf/refresh', { method: 'POST', body: JSON.stringify({ refreshToken }) }, false);
    const current = useAuthStore.getState();
    useAuthStore.getState().setSession({
      accessToken: data.tokens.accessToken,
      refreshToken: data.tokens.refreshToken,
      user: current.user!,
    });
    return true;
  } catch {
    return false;
  }
}

// ---- typed helpers per endpoint ----

export interface SignupInput {
  phone: string;
  email: string;
  fullName?: string;
  preferredLanguage: 'he' | 'en' | 'ar';
  pplConsentVersion: string;
}

export function signupRequest(input: SignupInput) {
  return apiAnon<{ message: string }>('/api/auth/carnaf/signup', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface VerifyResult {
  user: {
    id: string;
    tenantId: string;
    email: string | null;
    phone: string | null;
    fullName: string | null;
    preferredLanguage: string;
  };
  tokens: { accessToken: string; refreshToken: string };
}

export function verifyMagicLink(token: string) {
  return apiAnon<VerifyResult>('/api/auth/carnaf/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

/** Exchange a Google Sign-In ID-token credential for a Carnaf JWT pair. */
export function googleSignIn(credential: string) {
  return apiAnon<VerifyResult>('/api/auth/carnaf/oauth/google', {
    method: 'POST',
    body: JSON.stringify({ credential }),
  });
}

export interface VehicleSummary {
  id: string;
  placeId: string;
  vin: string;
  licensePlate: string | null;
  oem: string;
  model: string;
  year: number;
  customLabel: string | null;
  isDefault: boolean;
  status: 'ACTIVE' | 'PARKED' | 'RETIRED';
  createdAt: string;
}

export function listVehicles() {
  return api<{ items: VehicleSummary[]; total: number }>('/api/vehicles');
}

export function getVehicle(id: string) {
  return api<VehicleSummary & { tenantId: string; updatedAt: string }>(`/api/vehicles/${id}`);
}

export function requestUploadUrl(input: {
  /** Optional. Omit to let the backend classify the doc via Claude Vision. */
  documentType?: CarnafDocumentType;
  fileMimeType: string;
  fileSizeBytes: number;
  vehicleId?: string;
  /** Multi-file / cold-start: auto-create a car from a registration with no match. */
  autoCreateVehicle?: boolean;
}) {
  return api<{
    documentId: string;
    uploadUrl: string;
    expiresInSeconds: number;
    storageKey: string;
  }>('/api/documents/upload-url', { method: 'POST', body: JSON.stringify(input) });
}

export function logout() {
  return api<{ revoked: boolean }>('/api/auth/carnaf/logout', {
    method: 'POST',
    body: JSON.stringify({}),
  }).finally(() => useAuthStore.getState().clear());
}

// --- documents (OCR review) ---

export type CarnafDocumentType =
  | 'VEHICLE_REGISTRATION'
  | 'DRIVER_LICENSE'
  | 'INSURANCE_COMPULSORY'
  | 'INSURANCE_COMPREHENSIVE'
  | 'SERVICE_CONTRACT'
  | 'INSURANCE_POLICY' // legacy
  | 'OTHER';

export type CarnafOcrStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'MANUAL_REVIEW';

export interface DocumentSummary {
  id: string;
  documentType: CarnafDocumentType;
  fileMimeType: string;
  fileSizeBytes: number;
  virusScanStatus: 'PENDING' | 'CLEAN' | 'INFECTED' | 'SCAN_FAILED';
  ocrStatus: CarnafOcrStatus;
  ocrConfidence: number | null;
  reviewedByOwner: boolean;
  reviewedAt: string | null;
  createdAt: string;
  vehicleId: string | null;
  documentExpiresAt: string | null;
  documentIssuer: string | null;
  documentNumber: string | null;
}

export interface DocumentDetail extends DocumentSummary {
  storageKey: string;
  fileSha256: string | null;
  extractedData: Record<string, unknown> | null;
  ocrPerFieldConf: Record<string, number> | null;
  retainOriginalUntil: string | null;
}

export function getDocument(id: string) {
  return api<DocumentDetail>(`/api/documents/${id}`);
}

export function notifyUploaded(id: string) {
  return api<DocumentDetail>(`/api/documents/${id}/notify-uploaded`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function deleteDocument(id: string) {
  return api<{ deleted: boolean }>(`/api/documents/${id}`, {
    method: 'DELETE',
  });
}

export interface DocumentDownloadUrl {
  url: string;
  expiresInSeconds: number;
  mimeType: string;
  filename: string;
}

export function getDocumentDownloadUrl(id: string) {
  return api<DocumentDownloadUrl>(`/api/documents/${id}/download-url`);
}

export function deleteVehicleKb(vehicleId: string, kbId: string) {
  return api<{ deleted: boolean }>(`/api/vehicles/${vehicleId}/kb/${kbId}`, {
    method: 'DELETE',
  });
}

// --- Ask (conversational AI) ---

export interface AskCitation {
  documentId: string;
  documentType: string;
  documentName: string | null;
  documentDate: string | null;
}

export interface AskResponse {
  answer: string;
  citations: AskCitation[];
  car: {
    label: string;
    plate: string | null;
    placeId: string;
  };
}

export function askVehicle(vehicleId: string, question: string) {
  return api<AskResponse>(`/api/vehicles/${vehicleId}/ask`, {
    method: 'POST',
    body: JSON.stringify({ question }),
  });
}

// --- Enrichment (data.gov.il and future public sources) ---

export type EnrichmentStatus = 'ACTIVE' | 'INACTIVE' | 'CANCELLED' | 'NOT_FOUND' | 'ERROR';

export interface VehicleEnrichment {
  status: EnrichmentStatus;
  manufacturer: string | null;
  manufacturerHe: string | null;
  marketingName: string | null;
  year: number | null;
  fuelType: string | null;
  fuelTypeHe: string | null;
  color: string | null;
  colorHe: string | null;
  vin: string | null;
  tireFront: string | null;
  tireRear: string | null;
  pollutionGroup: number | null;
  engineCode: string | null;
  lastMotDate: string | null;
  registrationValidUntil: string | null;
  ownershipType: string | null;
  ownershipTypeHe: string | null;
  firstOnRoad: string | null;
}

export function getVehicleEnrichment(vehicleId: string) {
  return api<VehicleEnrichment | null>(`/api/vehicles/${vehicleId}/enrichment`);
}

export function refreshVehicleEnrichment(vehicleId: string) {
  return api<VehicleEnrichment | null>(`/api/vehicles/${vehicleId}/enrichment/refresh`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// --- Surfacing engine: ranked, tiered facts from free public data ---

export type FactTier = 'T0' | 'T1' | 'T2' | 'T3';
export type FactKind = 'test_expiry' | 'insurance_expiry' | 'recall' | 'pollution' | 'fuel_price';
export type FactEvent = 'impression' | 'tap' | 'dismiss' | 'complete';

export interface SurfacedFact {
  kind: FactKind;
  key: string;
  tier: FactTier;
  score: number;
  severity: 'safety' | 'legal' | 'financial' | 'convenience' | 'info';
  confidence: number;
  params: Record<string, unknown>;
}

export function getVehicleFacts(vehicleId: string) {
  return api<{ facts: SurfacedFact[]; asOf: string }>(`/api/vehicles/${vehicleId}/facts`);
}

export function logFactEvent(
  vehicleId: string,
  input: { kind: FactKind; key: string; event: FactEvent; tier?: FactTier },
) {
  return api<{ logged: boolean }>(`/api/vehicles/${vehicleId}/facts/event`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function listDocumentsForVehicle(vehicleId: string) {
  return api<{ items: DocumentSummary[]; total: number }>(`/api/documents?vehicleId=${encodeURIComponent(vehicleId)}`);
}

/** All of the caller's documents (across every car + unbound/personal). */
export function listDocuments() {
  return api<{ items: DocumentSummary[]; total: number }>(`/api/documents`);
}

export function confirmDocument(
  id: string,
  edits?: Record<string, unknown>,
  vehicleId?: string,
  documentType?: CarnafDocumentType,
) {
  return api<DocumentDetail>(`/api/documents/${id}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ edits, vehicleId, documentType }),
  });
}

// --- vehicle create + events ---

export interface VehicleCreateInput {
  vin: string;
  licensePlate?: string;
  oem: string;
  model: string;
  trim?: string;
  year: number;
  customLabel?: string;
  sourceDocumentId?: string;
}

export function createVehicle(input: VehicleCreateInput) {
  return api<VehicleSummary & { tenantId: string; updatedAt: string }>('/api/vehicles', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface VehicleUpdateInput {
  customLabel?: string;
  trim?: string;
  // Status changes are admin-only; CONSUMER_OWNER cannot change status here.
}

export function updateVehicle(id: string, input: VehicleUpdateInput) {
  return api<VehicleSummary>(`/api/vehicles/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteVehicle(id: string) {
  return api<{ deleted: boolean; graceUntil: string }>(`/api/vehicles/${id}`, {
    method: 'DELETE',
  });
}

/** Mark a vehicle as the household's default/primary car (one per household). */
export function setDefaultVehicle(id: string) {
  return api<VehicleSummary>(`/api/vehicles/${id}/set-default`, { method: 'POST', body: '{}' });
}

// ---- Family members (household co-owners) ----
export interface FamilyMember {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  isPrimaryOwner: boolean;
  isSelf: boolean;
  hasSignedIn: boolean;
  createdAt: string;
}

export function listFamilyMembers() {
  return api<{ members: FamilyMember[] }>(`/api/family/members`);
}

export function inviteFamilyMember(input: { name?: string; email: string; phone: string; preferredLanguage?: string }) {
  return api<{ member: FamilyMember }>(`/api/family/members`, { method: 'POST', body: JSON.stringify(input) });
}

export function updateFamilyMember(id: string, input: { name?: string }) {
  return api<{ member: FamilyMember }>(`/api/family/members/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export function removeFamilyMember(id: string) {
  return api<{ removed: boolean }>(`/api/family/members/${id}`, { method: 'DELETE' });
}

export interface VehicleEventRow {
  id: string;
  eventType: string;
  occurredAt: string;
  payload: unknown;
  resolvedByAi: boolean;
  routedToOperator: boolean;
}

export function listVehicleEvents(vehicleId: string) {
  return api<{ items: VehicleEventRow[]; total: number }>(`/api/vehicles/${vehicleId}/events`);
}

// --- permissions ---

export type PermissionScope = 'OWNER' | 'FULL' | 'DRIVE' | 'READ' | 'VIEW';

export interface GrantRow {
  id: string;
  vehicleId: string;
  tenantId: string;
  grantFromUserId: string;
  grantToUserId: string | null;
  grantToPhone: string;
  scope: PermissionScope;
  expiresAt: string | null;
  acceptedAt: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  invitationChannel: string | null;
  invitationSentAt: string | null;
  reminderSentCount: number;
  createdAt: string;
  updatedAt: string;
}

export function sendInvite(
  vehicleId: string,
  input: {
    grantToPhone: string;
    scope: Exclude<PermissionScope, 'OWNER'>;
    expiresAt?: string;
  },
) {
  return api<{ grant: GrantRow; invitationToken: string; acceptUrl: string }>(
    `/api/vehicles/${vehicleId}/permissions/grant`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function listGrantsForVehicle(vehicleId: string) {
  return api<{ items: GrantRow[]; total: number }>(`/api/vehicles/${vehicleId}/permissions`);
}

export function listGrantsReceived() {
  return api<{ items: GrantRow[]; total: number }>('/api/permissions/received');
}

export function revokeGrant(grantId: string, reason?: string) {
  return api<GrantRow>(`/api/permissions/${grantId}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export function acceptInvite(token: string) {
  return api<{ vehicleId: string; scope: PermissionScope; acceptedAt: string }>(`/api/permissions/accept/${token}`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// --- accident flow ---

export type AccidentFlowState =
  | 'ACCIDENT_INITIATED'
  | 'SAFETY_CHECK'
  | 'LOCATION_CAPTURED'
  | 'PHOTO_CAPTURE_IN_PROGRESS'
  | 'PARTY_EXCHANGE_OPTIONAL'
  | 'CLAIM_PREFILE'
  | 'DISPATCH'
  | 'RESOLVED'
  | 'ESCALATED_TO_HUMAN';

export type CaptureGuidance = 'front' | 'rear' | 'left' | 'right' | 'damage_close' | 'plate' | 'documents';

export interface AccidentFlowResult {
  flowId: string;
  state: AccidentFlowState;
  nextStep: string;
}

export interface InitializeResult extends AccidentFlowResult {
  disclaimerKey: string;
}

export interface ClaimPrefileResult extends AccidentFlowResult {
  claimReference: string;
}

export interface DispatchResult extends AccidentFlowResult {
  dispatchId: string;
  etaMinutes: number;
}

export interface EscalateResult extends AccidentFlowResult {
  escalationId: string;
  operatorContact: string;
}

export function accidentInitialize(input: {
  vehicleId: string;
  safetyCheckResult: boolean;
  language?: 'he' | 'en' | 'ar';
}) {
  return api<InitializeResult>('/api/accident/initialize', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function accidentUploadPhoto(input: {
  flowId: string;
  photoUrl: string;
  geo?: { lat: number; lng: number };
  captureGuidance?: CaptureGuidance;
}) {
  return api<AccidentFlowResult>('/api/accident/upload-photo', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function accidentExchangeParty(input: {
  flowId: string;
  partyData: {
    name?: string;
    plate?: string;
    insurerName?: string;
    insurancePolicy?: string;
    phone?: string;
  };
}) {
  return api<AccidentFlowResult>('/api/accident/exchange-party', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function accidentPrefileClaim(flowId: string) {
  return api<ClaimPrefileResult>('/api/accident/prefile-claim', {
    method: 'POST',
    body: JSON.stringify({ flowId }),
  });
}

export function accidentRequestTow(flowId: string, location: { lat: number; lng: number }) {
  return api<DispatchResult>('/api/accident/request-tow', {
    method: 'POST',
    body: JSON.stringify({ flowId, location }),
  });
}

export function accidentRequestReplacement(flowId: string, preferredLocation?: { lat: number; lng: number }) {
  return api<DispatchResult>('/api/accident/request-replacement', {
    method: 'POST',
    body: JSON.stringify({ flowId, preferredLocation }),
  });
}

export function accidentEscalate(flowId: string, reason: string) {
  return api<EscalateResult>('/api/accident/escalate', {
    method: 'POST',
    body: JSON.stringify({ flowId, reason }),
  });
}

// --- billing ---

export type CarnafSubscriptionPlan = 'FREE' | 'PREMIUM' | 'PREMIUM_ANNUAL';
export type CarnafSubscriptionStatus = 'ACTIVE' | 'TRIALING' | 'PAST_DUE' | 'CANCELED' | 'EXPIRED';

export interface SubscriptionView {
  tenantId: string;
  plan: CarnafSubscriptionPlan;
  status: CarnafSubscriptionStatus;
  currency: string;
  amountCents: number | null;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  stripeSubscriptionId: string | null;
}

export function getSubscription() {
  return api<SubscriptionView>('/api/billing/carnaf/subscription');
}

export function createCheckoutSession(input: {
  priceKey: 'PREMIUM_MONTHLY' | 'PREMIUM_ANNUAL';
  currency?: 'ILS' | 'USD';
  successUrl?: string;
  cancelUrl?: string;
}) {
  return api<{ checkoutUrl: string; sessionId: string }>('/api/billing/carnaf/checkout', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function createPortalSession(returnUrl?: string) {
  return api<{ portalUrl: string }>('/api/billing/carnaf/portal', {
    method: 'POST',
    body: JSON.stringify({ returnUrl }),
  });
}

// --- households ---

export type HouseholdMemberRole = 'PRIMARY' | 'SPOUSE' | 'CHILD' | 'EXTENDED' | 'MEMBER';

export interface HouseholdView {
  id: string;
  name: string;
  ownerUserId: string;
  members: Array<{
    id: string;
    userId: string;
    role: HouseholdMemberRole;
    joinedAt: string;
    leftAt: string | null;
  }>;
  createdAt: string;
  updatedAt: string;
}

export function listHouseholds() {
  return api<{ items: HouseholdView[]; total: number }>('/api/households');
}

export function createHousehold(name: string) {
  return api<HouseholdView>('/api/households', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export function getHousehold(id: string) {
  return api<HouseholdView>(`/api/households/${id}`);
}

export function renameHousehold(id: string, name: string) {
  return api<HouseholdView>(`/api/households/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
}

export function addHouseholdMember(id: string, userId: string, role: HouseholdMemberRole) {
  return api<HouseholdView>(`/api/households/${id}/members`, {
    method: 'POST',
    body: JSON.stringify({ userId, role }),
  });
}

export function removeHouseholdMember(id: string, memberId: string) {
  return api<{ removed: boolean }>(`/api/households/${id}/members/${memberId}`, {
    method: 'DELETE',
  });
}

// --- vehicle KB writes ---

export type VehicleKBLayer = 'OEM' | 'OWNER' | 'FLEET' | 'VEHICLE' | 'DRIVER';

export interface KBOverrideRow {
  id: string;
  vehicleId: string;
  layer: VehicleKBLayer;
  contentKey: string;
  contentValue: unknown;
  language: string;
  approvedBy: string;
  approvedAt: string;
  createdAt: string;
}

export function listVehicleKb(vehicleId: string) {
  return api<{ items: KBOverrideRow[]; total: number }>(`/api/vehicles/${vehicleId}/kb`);
}

export function writeVehicleKb(
  vehicleId: string,
  input: {
    layer: 'OWNER' | 'VEHICLE';
    contentKey: string;
    contentValue: unknown;
    language?: string;
  },
) {
  return api<KBOverrideRow>(`/api/vehicles/${vehicleId}/kb`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// Insurance claim-packet emit (built on top of the accident flow).
export function emitClaimPacket(flowId: string, carrierName?: string) {
  return api<{
    flowId: string;
    packetReference: string;
    emailed: boolean;
    emailMessageId?: string;
    carrierEmail?: string;
  }>('/api/insurance/emit-claim-packet', {
    method: 'POST',
    body: JSON.stringify({ flowId, carrierName }),
  });
}

// --- OBD-II diagnostics (Web Bluetooth scan persistence) ---

export interface ObdScanSummary {
  id: string;
  scannedAt: string;
  storedDtcCount: number;
  pendingDtcCount: number;
  readyForInspection: boolean | null;
  milOn: boolean | null;
}

/** Persist a completed OBD scan as a vehicle event. `scan` is the shared ObdScanResult. */
export function saveObdScan(vehicleId: string, scan: ObdScanResult) {
  return api<ObdScanSummary>(`/api/vehicles/${vehicleId}/obd/scan`, {
    method: 'POST',
    body: JSON.stringify(scan),
  });
}

/** Fetch the most recent OBD scan for a vehicle (or null if never scanned). */
export function getLatestObdScan(vehicleId: string) {
  return api<ObdScanResult | null>(`/api/vehicles/${vehicleId}/obd/latest`);
}
